import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './JewelCard.css';
import { trackEvent } from '../utils/analytics';
import { formatInrWhole, jewelGstCaption } from '../utils/money';

const MATERIAL_LABELS = {
  gold: 'Gold',
  silver: 'Silver',
  fancy: 'Fancy',
  gold_plated: 'Gold plated',
};

function primaryImageUrl(jewel) {
  if (!jewel.image_mime_type) return null;
  return `/api/jewels/${jewel.id}/image`;
}

function extraImageUrls(jewel) {
  return (jewel.extra_images || []).map((ex) => `/api/jewel-extra-images/${ex.id}`);
}

export default function JewelCard({ jewel, onAddToCart }) {
  const { items, updateQuantity, removeItem } = useCart();
  const line = items.find((i) => i.jewelId === jewel.id);
  const price = jewel.display_price;
  const materialLabel = MATERIAL_LABELS[jewel.material] || jewel.material;
  const gstLine = jewelGstCaption(jewel);

  const primaryUrl = useMemo(() => primaryImageUrl(jewel), [jewel]);
  const extraUrls = useMemo(() => extraImageUrls(jewel), [jewel]);
  const allUrls = useMemo(() => {
    const u = [];
    if (primaryUrl) u.push(primaryUrl);
    u.push(...extraUrls);
    return u;
  }, [primaryUrl, extraUrls]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [thumbsMediaEnabled, setThumbsMediaEnabled] = useState(false);
  const thumbsRef = useRef(null);

  const showThumbStrip = allUrls.length > 1;

  useEffect(() => {
    setActiveIdx(0);
    setThumbsMediaEnabled(false);
  }, [jewel.id]);

  useEffect(() => {
    const el = thumbsRef.current;
    if (!el || !showThumbStrip) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setThumbsMediaEnabled(true);
          obs.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [showThumbStrip, jewel.id]);

  const pickImage = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setThumbsMediaEnabled(true);
    setActiveIdx(idx);
  };

  const mainSrc =
    allUrls.length === 0
      ? null
      : activeIdx === 0 || thumbsMediaEnabled
        ? allUrls[activeIdx]
        : allUrls[0];

  return (
    <div className="jewel-card">
      <Link to={`/jewel/${jewel.id}`} className="jewel-card-link">
        <div className="jewel-card-gallery">
          <div className="jewel-card-image">
            {mainSrc ? (
              <img
                src={mainSrc}
                alt={jewel.name}
                loading="lazy"
                className="jewel-card-main-img"
              />
            ) : (
              <div className="jewel-card-placeholder">No Image</div>
            )}
            <span className={`jewel-card-badge ${jewel.material === 'gold_plated' ? 'gold-plated' : jewel.material}`}>{materialLabel}</span>
          </div>
          {showThumbStrip && (
            <div ref={thumbsRef} className="jewel-card-thumbs" role="group" aria-label="Product photos">
              {allUrls.map((url, i) => (
                <button
                  key={url}
                  type="button"
                  className={`jewel-card-thumb ${i === activeIdx ? 'is-active' : ''}`}
                  onClick={(e) => pickImage(e, i)}
                  aria-label={`Photo ${i + 1} of ${allUrls.length}`}
                  aria-current={i === activeIdx ? 'true' : undefined}
                >
                  {i === 0 || thumbsMediaEnabled ? (
                    <img src={url} alt="" loading="lazy" />
                  ) : (
                    <span className="jewel-card-thumb-skeleton" aria-hidden />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="jewel-card-info">
          {jewel.item_number && (
            <p className="jewel-card-item-no">Item #{jewel.item_number}</p>
          )}
          <h3>{jewel.name}</h3>
          {price != null ? (
            <p className="jewel-card-price">{formatInrWhole(price)}</p>
          ) : (
            <p className="jewel-card-price muted">Price unavailable</p>
          )}
          {gstLine && <p className="jewel-card-gst-line">{gstLine}</p>}
          {jewel.weight_g && jewel.material !== 'fancy' && (
            <p className="jewel-card-weight">{jewel.weight_g}g</p>
          )}
        </div>
      </Link>
      {onAddToCart && (
        <div className="jewel-card-enquiry-row">
          {!line ? (
            <button
              type="button"
              className="jewel-card-cart-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToCart(jewel);
                trackEvent('add_to_enquiry', {
                  item_id: jewel.id,
                  item_name: jewel.name,
                  material: jewel.material,
                  value: jewel.display_price || 0,
                });
              }}
            >
              + Add to Enquiry
            </button>
          ) : (
            <div className="jewel-card-qty" onClick={(e) => e.stopPropagation()}>
              <span className="jewel-card-qty-label">In enquiry</span>
              <div className="jewel-card-qty-controls">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (line.quantity <= 1) removeItem(jewel.id);
                    else updateQuantity(jewel.id, line.quantity - 1);
                  }}
                >
                  −
                </button>
                <span>{line.quantity}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateQuantity(jewel.id, line.quantity + 1);
                  }}
                >
                  +
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
